import { UITrackEventPayload } from '@atlassian/community-library';
import { ButtonAnalyticsAttributes } from './types';
import { ANALYTICS_SUBJECTS } from './constants';

/**
 * Generates a button click/interaction event payload with support for extended attributes.
 * 
 * @example
 * // Basic usage (backward compatible)
 * getButtonEventPayload('clicked', 'applyButton', 'heroSection', '/apply', 'Apply now')
 * 
 * @example
 * // Extended usage with Champions-specific attributes
 * getButtonEventPayload('clicked', 'applyButton', 'heroSection', '/apply', 'Apply now', {
 *   isAuthenticated: true,
 *   userId: 'user123',
 *   featureGateEnabled: true,
 *   entryPoint: 'heroSection',
 * })
 * 
 * @param action - The action type (e.g., 'clicked', 'hovered')
 * @param actionSubjectId - Unique identifier for the button
 * @param source - Source/section identifier for journey tracking
 * @param href - The button's destination URL
 * @param label - The visible button text
 * @param extendedAttributes - Optional extended attributes for enhanced tracking (Champions forms, auth state, etc.)
 * @returns UITrackEventPayload for the analytics service
 */
export const getButtonEventPayload = (
  action: string,
  actionSubjectId: string,
  source: string,
  href: string,
  label: string,
  extendedAttributes?: Partial<ButtonAnalyticsAttributes>,
): UITrackEventPayload => {
  return {
    action,
    actionSubject: ANALYTICS_SUBJECTS.BUTTON,
    actionSubjectId,
    source,
    attributes: {
      href,
      label,
      // Spread extended attributes if provided (backward compatible - empty by default)
      ...(extendedAttributes && {
        isAuthenticated: extendedAttributes.isAuthenticated,
        userId: extendedAttributes.userId,
        featureGateEnabled: extendedAttributes.featureGateEnabled,
        entryPoint: extendedAttributes.entryPoint,
      }),
    },
  } as UITrackEventPayload;
};
